import { useState } from 'react';
import SEO from '@/components/SEO';
import { site } from '@/data/content';
import { ArrowUpRight, Users, Eye, Search, MousePointer, BarChart3, LineChart, Activity, Globe, Check } from 'lucide-react';

const metrics = [
  { icon: Users, title: 'Live visitors', text: 'See how many visitors are on your site right now and which pages they are viewing.' },
  { icon: Eye, title: 'Page views', text: 'Track the most visited pages, average time on page, and bounce rates over any date range.' },
  { icon: Search, title: 'Keyword tracking', text: 'Discover the search terms and referral sources that bring visitors to your website.' },
  { icon: MousePointer, title: 'Click & tap maps', text: 'Understand what visitors interact with so you can improve buttons, links, and forms.' },
  { icon: BarChart3, title: 'Conversion overview', text: 'Measure how website visits turn into chats, signups, and qualified leads.' },
  { icon: LineChart, title: 'Performance trends', text: 'Compare traffic, engagement, and conversion trends across weeks and months.' },
];

const liveView = [
  { page: '/pricing', visitors: 12, change: '+4' },
  { page: '/features', visitors: 8, change: '+2' },
  { page: '/', visitors: 24, change: '+7' },
  { page: '/integrations', visitors: 5, change: '+1' },
];

const topKeywords = ['customer service software', 'AI support chat', 'WhatsApp help desk', 'live chat widget', 'omnichannel inbox'];

const faqs = [
  { q: 'What is included in ZuriHub website analytics?', a: 'The analytics suite includes live visitor counts, page views, keyword tracking, conversion tracking, and optional add-ons for Heatmaps ($15/workspace/month), Live Visitors, and Screen Recording.' },
  { q: 'Can I use analytics without the add-ons?', a: 'Yes. Core analytics is included in Pro and Enterprise plans. Add-ons extend the data with live visitor details, heatmap replays, and screen recordings.' },
  { q: 'How fresh is the live visitor data?', a: 'Live visitor data is updated in near real time through the edge tracker. Historical trends and device breakdowns refresh on page load.' },
  { q: 'Where is visitor data stored?', a: 'All data is stored in the workspace Cloudflare D1 database with 30-day retention for event-level data. Snapshots and recordings are stored in workspace R2 storage.' },
  { q: 'Is there a free trial?', a: 'New workspaces get a 14-day Pro trial. You can use Heatmaps and the other add-ons during the trial if they are enabled.' },
];

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'ZuriHub Website Analytics',
    description: 'Track live visitors, page performance, keyword tracking, and conversion trends inside your workspace. Built-in analytics with Heatmaps and Screen Recording add-ons.',
    brand: { '@type': 'Brand', name: 'ZuriHub' },
    offers: {
      '@type': 'Offer',
      url: `${site.baseUrl}/website-analytics`,
      price: '25',
      priceCurrency: 'USD',
      priceValidUntil: '2027-12-31',
      availability: 'https://schema.org/InStock',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  },
];

export default function Analytics() {
  const [timeRange, setTimeRange] = useState('24h');

  return (
    <>
      <SEO
        title="Website Analytics - Live Visitors & Performance Insights | ZuriHub"
        description="Track live visitors, page performance, keyword tracking, and conversion trends inside your workspace. Built-in analytics with Heatmaps and Screen Recording add-ons."
        path="/website-analytics"
        jsonLd={jsonLd}
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-20 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-7xl">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-4 py-1.5 text-sm font-medium text-slate-300 backdrop-blur">
                <Activity className="h-4 w-4" /> New feature
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Website analytics <span className="gradient-text">inside your workspace</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                Measure live visitors, track pages and keywords, and see how your site performs without leaving Zuri Hub.
              </p>
              <a
                href={site.workspaceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-bold text-slate-900 transition hover:bg-slate-100"
              >
                Launch workspace <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-600/20 to-brand-400/10 blur-3xl" />
              <div className="relative overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/60 p-2 backdrop-blur">
                <img
                  src="https://media.getzurihub.com/Jayrah%20Assets/Website%20Analytics.png"
                  alt="ZuriHub website analytics dashboard"
                  className="rounded-2xl w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 className="section-title">Everything your site tells you</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl">
              Real-time and historical insights so you can turn traffic into conversations.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {metrics.map((m) => (
              <div key={m.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <m.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{m.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{m.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <h2 className="text-3xl font-extrabold text-slate-900 sm:text-4xl">Live visitor overview</h2>
              <p className="mt-4 text-lg text-slate-600">
                See who is active on your site in real time. Pages update every few seconds so you never miss a hot lead.
              </p>
              <div className="mt-8 inline-flex gap-2 rounded-full border border-slate-200 bg-white p-1 shadow-sm">
                {['24h', '7d', '30d'].map((r) => (
                  <button
                    key={r}
                    onClick={() => setTimeRange(r)}
                    className={`rounded-full px-4 py-1.5 text-sm font-bold transition ${timeRange === r ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
            <div className="rounded-3xl bg-slate-900 p-6 text-white shadow-xl sm:p-8">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Live visitors now</p>
                  <p className="text-4xl font-extrabold">49</p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/20 text-green-400">
                  <Globe className="h-6 w-6" />
                </div>
              </div>
              <div className="mt-6 space-y-3">
                {liveView.map((p) => (
                  <div key={p.page} className="flex items-center justify-between rounded-xl bg-slate-800 p-3">
                    <span className="font-semibold">{p.page}</span>
                    <div className="flex items-center gap-3">
                      <span className="font-bold">{p.visitors}</span>
                      <span className="text-xs font-bold text-green-400">{p.change}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-16 rounded-3xl border border-slate-200 bg-white p-8 shadow-sm sm:p-10">
            <h3 className="text-xl font-bold text-slate-900">Top keywords bringing visitors</h3>
            <div className="mt-6 flex flex-wrap gap-3">
              {topKeywords.map((k) => (
                <span key={k} className="rounded-full bg-slate-100 px-4 py-2 text-sm font-bold text-slate-700">
                  {k}
                </span>
              ))}
            </div>
            <p className="mt-4 text-sm text-slate-500">
              Keyword tracking helps you understand what your audience is searching for so you can optimize content and capture more demand.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-slate-900 py-24 text-white" aria-labelledby="add-ons-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="add-ons-heading" className="text-3xl font-extrabold sm:text-4xl">New add-on analytics</h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-400">
              Go beyond counts. Add Live Visitors, Heatmaps, and Screen Recording to see exactly how visitors behave.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { title: 'Live Visitors', text: 'Real-time active users, top countries, cities, devices, and browsers with 30m/1h/7d/30d ranges.', price: 'Add-on', cta: 'Contact sales' },
              { title: 'Heatmaps', text: 'Record click, scroll, and pageview events. Replay sessions on the actual page snapshot captured by the tracker.', price: '$15/mo per workspace' },
              { title: 'Screen Recording', text: 'Request and capture a visitor screen with explicit browser consent to debug issues faster.', price: 'Add-on', cta: 'Contact sales' },
            ].map((a) => (
              <div key={a.title} className="rounded-3xl border border-slate-700 bg-slate-800 p-8 shadow-sm">
                <h3 className="text-lg font-bold">{a.title}</h3>
                <p className="mt-2 text-sm text-slate-400">{a.text}</p>
                <p className="mt-4 text-xl font-extrabold text-brand-400">{a.price}</p>
                {a.cta && <p className="mt-2 text-sm text-slate-500">{a.cta}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="how-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="how-heading" className="section-title">How it works</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl">
              Set up in minutes. One script gives you live, trend, and replay data in the same workspace.
            </p>
          </div>
          <div className="mt-14 grid gap-8 lg:grid-cols-3">
            {[
              { step: '1', title: 'Install the tracker', text: 'Add a single script tag to the head of your site. It is lightweight, async, and works with any website or SPA.' },
              { step: '2', title: 'Collect visitor events', text: 'Live Visitors, page views, clicks, scrolls, and heatmap snapshots are sent to your workspace. Data is retained for 30 days.' },
              { step: '3', title: 'View and replay', text: 'Open the Live Visitors or Heatmaps page to see real-time activity, trends, and replay sessions with the actual page snapshot.' },
            ].map((s) => (
              <div key={s.step} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-100 text-lg font-bold text-brand-700">{s.step}</span>
                <h3 className="mt-4 text-xl font-bold text-slate-900">{s.title}</h3>
                <p className="mt-2 text-slate-600">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="scale-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 id="scale-heading" className="section-title">Built for scale</h2>
              <p className="mt-4 text-lg text-slate-600">
                ZuriHub analytics runs on Zuri Cloud Edge. Fast-loading dynamic data refreshes every 10 seconds to give you live insights, while sessions and events stay indexed for fast queries. Live visitor sessions are batched, heatmap data is cleaned up automatically after 30 days, and the visitor table paginates so large workspaces stay fast.
              </p>
              <ul className="mt-8 space-y-3">
                {['Edge delivery for near real-time data', 'Indexed sessions and events', 'Auto-deletion after 30 days', 'Paginated tables for thousands of visitors', 'One tracker for all add-ons'].map((s) => (
                  <li key={s} className="flex items-start gap-2 text-slate-700">
                    <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" /> {s}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl bg-slate-900 p-8 text-white shadow-xl">
              <div className="grid grid-cols-2 gap-6">
                {[
                  { label: 'Live visitors tracked', value: 'Real-time' },
                  { label: 'Event retention', value: '30 days' },
                  { label: 'Heatmap replay', value: 'With snapshot' },
                  { label: 'Pricing model', value: 'Per workspace' },
                ].map((s) => (
                  <div key={s.label} className="rounded-2xl bg-slate-800 p-4">
                    <p className="text-xs text-slate-400">{s.label}</p>
                    <p className="mt-1 text-2xl font-bold">{s.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="use-cases-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="use-cases-heading" className="section-title">Use cases</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl">
              Teams use ZuriHub analytics to turn website traffic into action.
            </p>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { title: 'E-commerce conversion', text: 'See which product pages keep visitors engaged, where they drop off, and what they click before checkout.' },
              { title: 'SaaS onboarding', text: 'Replay new user sessions to find friction in onboarding flows and fix the steps that cause churn.' },
              { title: 'Support deflection', text: 'Identify pages that drive the most live chats and add targeted help content before visitors reach out.' },
            ].map((u) => (
              <div key={u.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
                <h3 className="text-lg font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-100 py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="faq-heading" className="section-title text-center">Frequently asked questions</h2>
          <p className="section-subtitle mx-auto mt-4 text-center max-w-2xl">
            Common questions about ZuriHub analytics and add-ons.
          </p>
          <div className="mt-10 space-y-4">
            {faqs.map((f, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h3 className="font-semibold text-slate-900">{f.q}</h3>
                <p className="mt-2 text-slate-600">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-slate-900 px-6 py-12 text-center text-white sm:px-12">
          <h2 className="text-2xl font-extrabold sm:text-3xl">Turn traffic into conversations</h2>
          <p className="mx-auto mt-4 max-w-2xl text-slate-300">
            Start with Pro analytics for $25/agent/month and add Heatmaps for $15/workspace when you are ready.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-brand-700 transition hover:bg-slate-100"
          >
            Launch workspace <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </>
  );
}
